/**
 * Minimal in-memory cache with a fixed lifetime per entry.
 *
 * Entries are grouped by site, so one site's entries can be cleared without touching
 * the other sites.
 *
 * The promise is cached rather than the value, so concurrent requests for the same
 * missing entry share one load. A failed load is removed again, so it is never cached.
 * Callers share the cached value and must not mutate it.
 */

type Entry = { promise: Promise<unknown>; expiresAt: number };
// Cache name -> siteId -> key -> entry
type Store = Map<string, Map<string, Map<string, Entry>>>;

// Kept on globalThis, so page renders and API routes (e.g. /api/purge clearing the
// cache) are guaranteed to use the same entries, even if Next.js loads this module
// more than once
const storeKey = Symbol.for('dia.memoryCaches');
const globalStore = globalThis as typeof globalThis & { [storeKey]?: Store };
// Reuse the store if an earlier load of this module created it, otherwise create it
const caches = (globalStore[storeKey] ??= new Map());

export const createMemoryCache = <T>(name: string, ttlInSeconds: number) => {
	if (!caches.has(name)) caches.set(name, new Map());
	const sites = caches.get(name);

	return ({ siteId, key = '' }: { siteId: string; key?: string }, load: () => Promise<T>): Promise<T> => {
		if (ttlInSeconds <= 0) return load();

		if (!sites.has(siteId)) sites.set(siteId, new Map());
		const entries = sites.get(siteId);

		const entry = entries.get(key);
		if (entry && entry.expiresAt > Date.now()) return entry.promise as Promise<T>;

		const promise = load();
		entries.set(key, { promise, expiresAt: Date.now() + ttlInSeconds * 1000 });
		promise.catch(() => {
			if (entries.get(key)?.promise === promise) entries.delete(key);
		});
		return promise;
	};
};

// Removes the entries of the site from all caches, so its next requests load fresh
// data. Requests already waiting for a load in flight still get its result.
export const clearAllMemoryCachesForSite = (siteId: string): void => caches.forEach((sites) => sites.delete(siteId));

// Removes the entries of the site from the named cache only. A cache is only created
// once the module using it is loaded, so until then there is nothing to remove.
export const clearMemoryCacheForSite = (name: string, siteId: string): void => {
	caches.get(name)?.delete(siteId);
};

// Removes the entries of all sites from the named cache only
export const clearMemoryCache = (name: string): void => {
	caches.get(name)?.clear();
};

// Removes the entries of all sites from all caches
export const clearMemoryCaches = (): void => caches.forEach((sites) => sites.clear());
